/**
 * What a finance lead needs to know about a month of claims.
 *
 * The book puts three questions on one screen — what is waiting on me, what the
 * company is spending, and where it goes — so the arithmetic behind all three
 * lives here rather than inside the screen. Extracting it is also the only way
 * to test the two rules that actually cost money if they are wrong: which claims
 * are over policy, and which are missing a receipt.
 *
 * Every day comparison is done on a UTC calendar month key. Deriving the month
 * from a local `Date` puts a claim submitted late on the 31st into the next
 * month for anyone east of UTC, which is exactly the class of bug that has
 * bitten this codebase repeatedly.
 */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "2026-03" for any ISO timestamp or date string. */
export const monthKey = (value) => {
  if (!value) return "";
  const s = String(value);
  // An ISO string already starts with the UTC calendar date; slicing beats
  // constructing a Date and reading local fields back out of it.
  if (/^\d{4}-\d{2}/.test(s)) return s.slice(0, 7);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

/** "March 2026" — spelled from a table, because ICU renders "Sept" for Sep. */
export const monthLabel = (key, { short = false } = {}) => {
  const [y, m] = String(key || "").split("-");
  const i = Number(m) - 1;
  if (!y || Number.isNaN(i) || i < 0 || i > 11) return "";
  const name = MONTHS[i];
  return short ? name : `${FULL[i]} ${y}`;
};

/** The month key `n` months before `key` (n may be negative). */
export const shiftMonth = (key, n) => {
  const [y, m] = String(key || "").split("-").map(Number);
  if (!y || !m) return "";
  const total = y * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
};

/** The `count` month keys ending at `key`, oldest first. */
export const monthsEnding = (key, count) =>
  Array.from({ length: count }, (_, i) => shiftMonth(key, i - (count - 1))).filter(Boolean);

/**
 * The two rules that block a reimbursement.
 *
 * Both read the category's own `limits`, which is the server's source of truth
 * for enforcement. A category with no limit set (`claimLimit: 0`) cannot be
 * exceeded — 0 means "no limit", not "nothing is allowed", and reading it the
 * other way would put every claim in the company over policy.
 */
export const isOverPolicy = (claim) => {
  const limit = Number(claim?.limits?.claimLimit || 0);
  return limit > 0 && Number(claim?.amount || 0) > limit;
};

export const isMissingReceipt = (claim) =>
  Boolean(claim?.limits?.receiptRequired) && !claim?.receiptUrl;

/**
 * Reimbursed is derived, not stored: the status enum is pending/approved/
 * rejected, and payment is a separate fact. So a reimbursed claim is still an
 * approved one, which is why the book's counts nest (74 approved, 61 of them
 * reimbursed) rather than partition.
 */
export const isReimbursed = (claim) =>
  claim?.status === "approved" && Boolean(claim?.reimbursedAt || claim?.reimbursedInPayroll);

/** Shape one server record into the row every part of this screen reads. */
export const normaliseClaim = (item, employee) => {
  const category = item?.policyCateogary || {};
  const claim = {
    _id: item?._id,
    submittedAt: item?.createdAt,
    month: monthKey(item?.createdAt),
    employeeName: employee
      ? `${employee.fname || ""} ${employee.lname || ""}`.trim() || "Unknown"
      : "Unknown",
    employee,
    category: category.name || "Uncategorised",
    categoryId: String(category._id || category.name || "uncategorised"),
    amount: Number(item?.amount || 0),
    status: item?.status,
    receiptUrl: item?.billUrl || "",
    limits: category.limits || {},
    reimbursedAt: item?.reimbursedAt,
    reimbursedInPayroll: item?.reimbursedInPayroll,
    quantity: item?.quantity,
    claimType: item?.claimType,
  };
  claim.overPolicy = isOverPolicy(claim);
  claim.missingReceipt = isMissingReceipt(claim);
  claim.reimbursed = isReimbursed(claim);
  return claim;
};

/**
 * A claim's displayed state. Over policy outranks pending, because it is the
 * thing the approver has to act on differently — a pending claim can be waved
 * through in a batch and this one cannot.
 */
export const claimState = (c) => {
  if (c?.status === "pending" && c?.overPolicy) return "overPolicy";
  if (c?.reimbursed) return "reimbursed";
  return c?.status;
};

const sum = (rows) => rows.reduce((t, r) => t + Number(r.amount || 0), 0);

/** Whole days between submission and reimbursement, or null if not paid yet. */
export const daysToReimburse = (c) => {
  if (!c?.reimbursedAt || !c?.submittedAt) return null;
  const a = Date.parse(c.submittedAt);
  const b = Date.parse(c.reimbursedAt);
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null;
  return (b - a) / 86400000;
};

export const median = (values) => {
  const v = values.filter((n) => typeof n === "number" && !Number.isNaN(n)).sort((a, b) => a - b);
  if (!v.length) return null;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
};

/**
 * The six counters. The book: "the two that carry colour are the two that block
 * a reimbursement" — over policy and missing receipts. The rest are plain.
 *
 * `pending` is deliberately not scoped to the month: a claim submitted in
 * February that nobody has decided is still waiting on you in March, and hiding
 * it behind a month picker is how a claim goes unpaid for a quarter.
 */
export const summarise = (rows, month) => {
  const inMonth = rows.filter((r) => r.month === month);
  const pending = rows.filter((r) => r.status === "pending");
  const overPolicy = pending.filter((r) => r.overPolicy);
  const missingReceipt = pending.filter((r) => r.missingReceipt);
  const reimbursed = inMonth.filter((r) => r.reimbursed);

  const thisMedian = median(reimbursed.map(daysToReimburse));
  const prevReimbursed = rows.filter((r) => r.month === shiftMonth(month, -1) && r.reimbursed);
  const prevMedian = median(prevReimbursed.map(daysToReimburse));

  return {
    pending: {
      amount: sum(pending),
      count: pending.length,
      people: new Set(pending.map((r) => r.employeeName)).size,
    },
    overPolicy: { amount: sum(overPolicy), count: overPolicy.length },
    missingReceipt: { count: missingReceipt.length },
    reimbursed: { amount: sum(reimbursed), count: reimbursed.length },
    timeToReimburse: { median: thisMedian, previous: prevMedian, previousMonth: shiftMonth(month, -1) },
    averageClaim: reimbursed.length ? sum(reimbursed) / reimbursed.length : null,
  };
};

/** Status tallies for the chips. Reimbursed nests inside approved. */
export const statusCounts = (rows) => ({
  all: rows.length,
  pending: rows.filter((r) => r.status === "pending").length,
  approved: rows.filter((r) => r.status === "approved").length,
  rejected: rows.filter((r) => r.status === "rejected").length,
  reimbursed: rows.filter((r) => r.reimbursed).length,
});

/**
 * "Claimed by month · approved and reimbursed."
 *
 * A pending claim is not yet a cost, and a rejected one never was, so neither
 * belongs in a spend chart.
 *
 * The book's caption also says "excluding advances", which this cannot honour:
 * an advance is not a distinguishable thing in the data. `claimType` is
 * flat/mileage/perDiem, and "Travel advance" in the book's own mockup is a
 * category *name* an organisation happens to have chosen. Filtering on that
 * string would silently drop a category from the spend chart in any company
 * that words it differently, so the caption states what is actually counted.
 */
export const claimedByMonth = (rows, endMonth, count = 8) => {
  const keys = monthsEnding(endMonth, count);
  const counted = rows.filter((r) => r.status === "approved");
  return keys.map((key) => ({
    key,
    label: monthLabel(key, { short: true }),
    amount: sum(counted.filter((r) => r.month === key)),
  }));
};

/** Month-on-month change as a signed fraction, or null when there is no base. */
export const monthChange = (bars) => {
  if (bars.length < 2) return null;
  const last = bars[bars.length - 1].amount;
  const prev = bars[bars.length - 2].amount;
  if (!prev) return null;
  return (last - prev) / prev;
};

/**
 * Where it goes. Sorted by spend so the largest category is first, with a tail
 * folded into "Other" — a list of nineteen categories answers nothing.
 */
export const categoryShare = (rows, month, { top = 4 } = {}) => {
  const counted = rows.filter((r) => r.month === month && r.status === "approved");
  const total = sum(counted);
  const byCategory = new Map();
  counted.forEach((r) => {
    const prev = byCategory.get(r.category) || 0;
    byCategory.set(r.category, prev + Number(r.amount || 0));
  });
  const sorted = [...byCategory.entries()]
    .map(([name, amount]) => ({ name, amount }))
    .sort((a, b) => b.amount - a.amount);

  const head = sorted.slice(0, top);
  const tail = sorted.slice(top);
  const rest = tail.reduce((t, c) => t + c.amount, 0);
  const shown = rest > 0 ? [...head, { name: "Other", amount: rest, isOther: true }] : head;

  return {
    total,
    rows: shown.map((c, i) => ({
      ...c,
      share: total ? c.amount / total : 0,
      // The palette is positional and stable, so a category keeps its colour
      // between the bar and the table — the same rule Reporting uses.
      colorIndex: i,
    })),
  };
};

/**
 * Bulk approval is offered only when every selected claim is under policy.
 * An over-policy claim has to be opened and decided on its own, so a selection
 * containing one is not partially approvable — it is not approvable.
 */
export const bulkApprovable = (selectedIds, byId) => {
  const ids = [...(selectedIds || [])];
  if (!ids.length) return { ok: false, count: 0, amount: 0, blocked: [] };
  const claims = ids.map((id) => byId.get(id)).filter(Boolean);
  const blocked = claims.filter((c) => c.overPolicy || c.status !== "pending");
  return {
    ok: blocked.length === 0 && claims.length > 0,
    count: claims.length,
    amount: sum(claims),
    blocked,
  };
};

/**
 * Money in the counters is exact; money in the chart is abbreviated, because
 * nobody quotes a bar.
 */
export const abbreviate = (n, symbol = "₹") => {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  if (abs >= 10000000) return `${symbol}${(v / 10000000).toFixed(abs >= 100000000 ? 0 : 1)}Cr`;
  if (abs >= 100000) return `${symbol}${(v / 100000).toFixed(abs >= 1000000 ? 0 : 1)}L`;
  if (abs >= 1000) return `${symbol}${(v / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  return `${symbol}${Math.round(v)}`;
};
